import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { TableConfig } from '@app/shared/util/table-config.utils';

import { ToolService } from '../../../../core/services/tool.service';
import { Log } from "ng2-logger";
import { ToolFormModel } from "@app/tool/tool-form-modal/tool-create.model";
import { Subscription } from 'rxjs';
import { ObservableService } from '@app/core/services/observable.service';
import { ToolsUsageTableComponent } from './tools-usage-table/tools-usage-table.component';
import { ToolUsageService } from '@app/core/services/tool-usage.service';
import { ToolUsageGroup } from '@app/tool/model/tool-usage-group.model';
import { TicketDetailsModalComponent } from '../ticket-management/components';
import { Ticket } from '@app/shared/models/ticket.model';
import { TicketService } from '@app/core/services/ticket.service';
import { ToastsManager } from "@app/shared/util/toastr.service";
import { ConfirmationModalComponent } from '@app/shared/components';
import { TranslateParser, TranslateService } from '@ngx-translate/core';
import { RevisionInfo } from "@components/revision-info/models/revision-info.model";
import { RecurrenceModalComponent } from "@app/roles/admin/pages/tools-usage/components/recurrence-modal/recurrence-modal.component";

@Component({
    selector: 'app-tools-list',
    templateUrl: 'tools-usage.component.html',
    styleUrls: ['tools-usage.component.scss']
})
export class ToolUsageComponent implements OnInit, OnDestroy {
    _logger = Log.create(ToolUsageComponent.name);
    @ViewChild(ToolsUsageTableComponent, {static: true}) adminToolsTable: ToolsUsageTableComponent;
    @ViewChild(TicketDetailsModalComponent, {static: true}) ticketDetailsModal: TicketDetailsModalComponent;
    @ViewChild(ConfirmationModalComponent, {static: true}) confirmationModal: ConfirmationModalComponent;
    @ViewChild(RecurrenceModalComponent, {static: true}) recurrenceModal: RecurrenceModalComponent;

    isLoadingTools = true;
    isLoadingRevision = false;

    toolsGrouped = [];
    tableConfig = new TableConfig();

    scannedTools = [];
    toolSet;
    selectedTool = new ToolFormModel();
    selectedTicket: Ticket;

    filterBySecLines = false;
    filterBySecLinesId = undefined;
    toolServiceSubscription: Subscription;
    observableServiceSubscription: Subscription;
    isFilterExpanded = true;

    constructor(private toolService: ToolService,
                private toastr: ToastsManager,
                private ticketService: TicketService,
                private router: Router,
                private translationService: TranslateService,
                private translateParser: TranslateParser,
                private toolUsageService: ToolUsageService,
                observableService: ObservableService) {
        this.observableServiceSubscription = observableService.hasChanged$.subscribe(
            () => {
                this.getTools(this.tableConfig);
            }
        )
    }

    ngOnInit() {
        this.toolServiceSubscription = this.toolService.onReloadTools().subscribe(() => {
            this.getTools(this.tableConfig);
        });
    }

    ngOnDestroy() {
        this.toolServiceSubscription.unsubscribe();
        this.observableServiceSubscription.unsubscribe();
    }

    onPageChange(tableState) {
        this._logger.info("onPageChange::", tableState);
        this.getTools(tableState);
    }

    onSortChange(tableState) {
        this._logger.info("onSortChange::", tableState);
        this.getTools(tableState);
    }

    onFilterChange(tableState) {
        this._logger.info("onFilterChange::", tableState);
        this.getTools(tableState);
    }

    onFilterGroupChange(tableState) {
        this.filterBySecLinesId = undefined;
        const lineFilter = this.tableConfig.filters.find(x => x.field === 'lineId');
        if (lineFilter) {
            this.filterBySecLinesId = lineFilter.value;
        }
        this.getTools(tableState);
    }

    private getTools(tableConfig: TableConfig) {
        this.toolsGrouped = [];
        this.isLoadingTools = true;
        this.toolUsageService.getTools(tableConfig).subscribe(
            (data) => {
                this._logger.info('getToolUsage:: Success ', data);
                const groupedData = [];
                const groupedTools = Array.from(this.groupBy(data.items, x => x.toolName));
                for (let i = 0; i < groupedTools.length; i++) {
                    if (groupedTools[i][1].length === 1) {
                        groupedData.push(groupedTools[i][1][0]);
                    } else {
                        const group = new ToolUsageGroup({
                            toolName: groupedTools[i][1][0].toolName,
                            toolDetailsId: groupedTools[i][1][0].toolDetailsId,
                            lineName: groupedTools[i][1][0].lineName
                        });
                        for (let j = 0; j < groupedTools[i][1].length; j++) {
                            groupedTools[i][1][j].parent = group;
                            group.cleaningPercentage += groupedTools[i][1][j].cleaningPercentage;
                            group.measurementsPercentage += groupedTools[i][1][j].measurementsPercentage;
                            group.maintenancePercentage += groupedTools[i][1][j].maintenancePercentage;
                        }
                        group.children = groupedTools[i][1];
                        group.cleaningPercentage =
                            Math.round((group.cleaningPercentage / groupedTools[i][1].length + Number.EPSILON) * 100) / 100;
                        group.measurementsPercentage =
                            Math.round((group.measurementsPercentage / groupedTools[i][1].length + Number.EPSILON) * 100) / 100;
                        group.maintenancePercentage =
                            Math.round((group.maintenancePercentage / groupedTools[i][1].length + Number.EPSILON) * 100) / 100;
                        groupedData.push(group);
                    }
                }
                this.toolsGrouped = groupedData;
                this.tableConfig.pager.count = data.count;
                this.isLoadingTools = false;
            },
            (error) => {
                this._logger.error('getToolUsage:: Error ', error);
            }
        );
    }

    openToolDetails(tool) {
        this.router.navigate([`tool-usage/${tool.toolId}`]);
    }

    openToolDetailsNewPage(tool) {
        const link = `tool-usage/${tool.id}`;
        localStorage.setItem('openedInNewTab', 'true');
        window.open(link);
    }

    onOpenTicket(ticketId: number) {
        const ticketDetailsUrl = this.router.serializeUrl(
            this.router.createUrlTree([`/ticket-management/${ticketId}/details`])
        );
        localStorage.setItem('openedInNewTab', 'true');
        window.open(ticketDetailsUrl, '_blank');
    }

    onCancelTicket(ticket) {
        this._logger.info("confirmCloseTicket:: Open 'close confirmation popup' for ticket:", this.selectedTicket);
        this.selectedTicket = ticket;
        this.ticketDetailsModal.close();
        this.confirmationModal.open();
    }

    confirmCancelTicket(event) {
        this._logger.info("onCloseTicket:: Scan badge successfully: ", event);
        this.confirmationModal.close();
        this._logger.info("onCloseTicket:: Closed confirmation modal");
        this.ticketService.closeTicket(this.selectedTicket, event.boschId).subscribe(
            (data: any) => {
                this._logger.info("onCloseTicket:: Ticket was deleted", this.selectedTicket);
                this.getTools(this.tableConfig);
                this.toastr.warning(this.buildCloseMessage(this.selectedTicket.id));
                this.ticketDetailsModal.close();
            },
            (error: any) => {
                this._logger.info("onCloseTicket:: API returned error", error);
            }
        );
    }

    onDownloadFile(fileToDownload: any) {
        this.ticketService.downloadFile(fileToDownload)
    }

    onOpenRevisionModal(toolInfo: { toolId: number, priority: number }) {
        this.isLoadingRevision = true;

        this.toolUsageService.getProductionData([toolInfo.toolId], toolInfo.priority).subscribe(
            (data: RevisionInfo []) => {
                if (data && data.length) {
                    this.recurrenceModal.open(data[0]);
                }
                this.isLoadingRevision = false;
            });
    }

    private groupBy(list: any[], keyGetter: any) {
        const map = new Map();
        list.forEach((item) => {
            const key = keyGetter(item);
            const collection = map.get(key);
            if (!collection) {
                map.set(key, [item]);
            } else {
                collection.push(item);
            }
        });
        return map;
    }

    private getTicketById(ticketId: number): void {
        this.isLoadingTools = true
        this.ticketService.getTicketById(ticketId).subscribe(
            (data: Ticket) => {
                this._logger.info('getTicketById:: API returned data ', data);
                this.selectedTicket = data;
                this.ticketDetailsModal.open(this.selectedTicket);
                this.isLoadingTools = false;
            },
            (error: any) => {
                this._logger.info('getTicketById:: API returned an error', error);
            }
        )
    }

    private buildCloseMessage(ticketId: number) {
        const message = this.translationService.instant('admin.tickets.toasts.closed');
        return this.translateParser.interpolate(message, {ticketId: ticketId});
    }

    toggleFilters() {
        this.isFilterExpanded = !this.isFilterExpanded;
    }
}
